import type { FC } from 'react';
import React, { memo } from 'react';
import styled from 'styled-components';
import { TagGroup } from '@oneaudi/unified-web-components';
import { SYS_SPACE_FIXED_100 } from '@oneaudi/unified-web-common';
import { useFilterContext } from '../../context/FilterContext';
import { FilterTag } from './filterTag';
import type { ActiveFilter } from '../../hooks/useFilters';
import { useUniversalState } from '../../context/UniversalStateContext';

// hide on desktop
const ActiveFilterBar = styled.div`
  display: block;
  padding-bottom: ${({ theme }) => theme.spacing.fixed200};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    display: none;
  }
`;

const StyledTagGroup = styled(TagGroup)`
  gap: ${SYS_SPACE_FIXED_100};
`;
// use row/div only when only 1 filter is active
//  if 2 or more are active use TagGroup component from desOps
const Row = styled.div`
  width: 100%;
`;

const ActiveFilterTags: FC = () => {
  const { hasActiveFilters, activeFilters, removeActiveFilter } = useFilterContext();
  const { translations } = useUniversalState();
  const getLabel = (type: ActiveFilter['type'], id: string) => translations[`${type}.${id}`] ?? id;
  const count = activeFilters.length;
  const firstFilter = activeFilters[0];
  if (!hasActiveFilters || activeFilters.length === 0) return null;

  return (
    <ActiveFilterBar>
      {count >= 2 ? (
        <StyledTagGroup size="sm">
          {activeFilters.map((f) => (
            <FilterTag
              key={`${f.type}-${f.id}`}
              id={`${f.type}-${f.id}`}
              text={getLabel(f.type, f.id)}
              size="sm"
              onClickHandler={() => removeActiveFilter(f)}
              dataTestId={`activeFilter-${f.type}-${f.id}`}
              variant="removable"
            />
          ))}
        </StyledTagGroup>
      ) : (
        <Row>
          <FilterTag
            id={`${firstFilter.type}-${firstFilter.id}`}
            text={getLabel(firstFilter.type, firstFilter.id)}
            size="sm"
            onClickHandler={() => removeActiveFilter(firstFilter)}
            dataTestId={`activeFilter-${firstFilter.type}-${firstFilter.id}`}
            variant="removable"
          />
        </Row>
      )}
    </ActiveFilterBar>
  );
};

export default memo(ActiveFilterTags);
